import { useTranslation } from "react-i18next";
import { ShopScopeSelect } from "@/components/layout/shop-scope-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";

export function SmartCreationSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <Skeleton className="h-14 w-[520px]" />
      <Skeleton className="h-[420px] w-full rounded-md" />
    </div>
  );
}

/** Products-step placeholder shown while the creation Shop switches. */
export function SmartCreationProductTransitionSkeleton({ shopId }: { shopId: string }) {
  const { t } = useTranslation("smartCreation");
  return (
    <div className="flex w-full flex-col gap-4" data-testid="smart-creation-shop-transition">
      <div className="flex justify-end">
        <div className="flex shrink-0 items-center gap-2">
          <span role="status" className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Spinner className="size-3.5" />
            {t("products.shopSelection.changing")}
          </span>
          <ShopScopeSelect
            selectionMode="single"
            value={shopId}
            disabled
            ariaLabel={t("products.shopSelection.label")}
            className="w-72 max-w-full"
          />
        </div>
      </div>
      <div className="grid min-h-[430px] grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-8 min-w-[220px] flex-1" />
            <Skeleton className="h-8 w-44" />
            <Skeleton className="h-8 w-24" />
          </div>
          <section className="overflow-hidden rounded-[10px] border border-line bg-surface">
            <div className="flex h-11 items-center gap-6 border-b border-line px-4">
              <Skeleton className="size-4" />
              <Skeleton className="h-3 w-48" />
              <Skeleton className="ml-auto h-3 w-16" />
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-14" />
            </div>
            <div className="flex flex-col">
              {[0, 1, 2, 3, 4].map((row) => (
                <div
                  key={row}
                  className="flex h-[68px] items-center gap-3 border-b border-line/70 px-4 last:border-b-0"
                >
                  <Skeleton className="size-4 shrink-0" />
                  <Skeleton className="size-11 shrink-0 rounded-[8px]" />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Skeleton className="h-3 w-3/5" />
                    <Skeleton className="h-2.5 w-2/5" />
                  </div>
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-3 w-14" />
                  <Skeleton className="h-3 w-10" />
                </div>
              ))}
            </div>
          </section>
        </div>
        <section className="overflow-hidden rounded-[10px] border border-line bg-surface">
          <div className="flex h-11 items-center justify-between border-b border-line px-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-14" />
          </div>
          <div className="flex flex-col gap-3 p-4">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-3">
                <Skeleton className="size-10 shrink-0 rounded-[8px]" />
                <div className="flex flex-1 flex-col gap-2">
                  <Skeleton className="h-3 w-4/5" />
                  <Skeleton className="h-2.5 w-3/5" />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <Skeleton className="h-[156px] w-full rounded-[10px]" />
    </div>
  );
}
